import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import { Actions as AUTHENTICATIONACTIONS } from '../../store/actions/authenticationActions';
import { Actions as PROFILEACTIONS } from '../../store/actions/profileActions';
import { Actions as SIDEBARACTIONS } from '../../store/actions/sidebarActions';
import { Actions as CONN_ACTIONS } from '../../store/actions/connectionActions';
import { Link, useLocation } from 'react-router-dom';
import AdminPortalSideBar from './AdminPortalSideBar';
import { BetaTag } from './ReusableComponents/Reusable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { ChevronDown, LayoutDashboard, Building2, MapPin, BarChart3, FileBarChart, Users, CircleUser, Terminal, Target, LifeBuoy, PlayCircle, CirclePlay, Lightbulb, Clipboard, Info, CircleHelp, Mail, FileText, CreditCard, Plug, Package, Trophy, Inbox, HeartHandshake } from 'lucide-react';
import { hasFeatureEntitlement, hasUsableSubscription } from '../utils/subscriptionAccess';
import {
  isClientWorkingContext,
  isGroupParentOrg,
  shellKindFromState,
} from '../utils/managementShell';

const Sidebar = ({
  show,
  branding,
  cyfAdmin,
  whiteLabelAdmin,
  consultantAdmin,
  isLocal,
  toggleAdminProfile,
  showAdminPortal,
  sidebarOpt,
  openModal,
  features,
  availableFeatures,
  getImportSummary,
}) => {
  const location = useLocation();
  const onAdminRoute = location.pathname.startsWith('/admin');
  const [state, setState] = useState({ admin: !!cyfAdmin, whiteLabelAdmin: !!whiteLabelAdmin });
  // Deep links into /admin/* should show the Admin Portal nav, not the customer shell.
  const [adminView, setAdminView] = useState(onAdminRoute);

  useEffect(() => {
    setState({ admin: !!cyfAdmin, whiteLabelAdmin: !!whiteLabelAdmin });
  }, [cyfAdmin, whiteLabelAdmin]);

  useEffect(() => {
    if (onAdminRoute) setAdminView(true);
  }, [onAdminRoute]);

  useEffect(() => {
    getImportSummary?.();
  }, [getImportSummary]);

  const { sidebarBackground, sidebarLinkColor } = branding.colors || {};

  const sidebarClass = `nav-collapse ${!show && `hide-left-bar`}`;

  if (adminView) return <AdminPortalSideBar adminView={adminView} setAdminView={setAdminView} />;

  return (
    <aside>
      <div id='sidebar' className={sidebarClass}>
        <div className='leftside-navigation'>
          <ul className='sidebar-menu admin-view' id='nav-accordion' style={{ backgroundColor: sidebarBackground }}>
            <div>
              {Object.keys(sidebarOpt)
                .filter((opt) => {
                  if (cyfAdmin) {
                    return sidebarOpt[opt].exclusiveFor?.includes(cyfAdmin) || !sidebarOpt[opt].exclusiveFor;
                  }
                  if (whiteLabelAdmin) {
                    return sidebarOpt[opt].exclusiveFor?.includes(whiteLabelAdmin) || !sidebarOpt[opt].exclusiveFor;
                  }
                  if (consultantAdmin) {
                    return sidebarOpt[opt].exclusiveFor?.includes(consultantAdmin) || !sidebarOpt[opt].exclusiveFor;
                  }
                  return !sidebarOpt[opt].exclusiveFor;
                })
                .filter((opt) => {
                  //check if feature is enabled by default, if not check if user has access to the feature
                  if (sidebarOpt[opt]?.feature) {
                    // Always show dashboard regardless of feature access
                    if (sidebarOpt[opt]?.feature === 'DASHBOARD') {
                      return sidebarOpt[opt];
                    }

                    let feature = features?.find((f) => f.featureKey === sidebarOpt[opt]?.feature);
                    if (feature?.enabledByDefault === true) {
                      return sidebarOpt[opt];
                    } else if (feature?.enabledByDefault === false && availableFeatures?.includes(feature?.featureKey)) {
                      return sidebarOpt[opt];
                    }
                    return false;
                  } else {
                    return sidebarOpt[opt];
                  }
                })
                .map((key, index) => {
                  const type = sidebarOpt[key].type;
                  const feature = sidebarOpt[key]?.feature;
                  const isBeta = features?.find((f) => f.featureKey === feature)?.isBeta;
                  const betaTag = feature && isBeta === true && <BetaTag />;
                  return (
                    <li key={index}>
                      {type === 'link' && (
                        <Link
                          key={index}
                          id={sidebarOpt[key].id}
                          to={sidebarOpt[key].to}
                          className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5 [&.active]:text-sidebar-primary [&.active]:border-l-sidebar-primary [&.active]:bg-sidebar-accent'
                          style={sidebarOpt[key].style}>
                          <div className='flex items-center gap-2'>
                            {sidebarOpt[key].icon}
                            <span>{sidebarOpt[key].title}</span>
                            {betaTag}
                          </div>
                        </Link>
                      )}

                      {type === 'mailto' && (
                        <a key={index} href={sidebarOpt[key].href} target='_blank' className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5' style={sidebarOpt[key].style}>
                          {sidebarOpt[key].icon}
                          <span>{sidebarOpt[key].title}</span>
                        </a>
                      )}
                      {type === 'accordion' && (
                        <Collapsible key={index} className='sidebar-accordion'>
                          <CollapsibleTrigger className='sidebar-label list flex w-full min-w-0 items-center justify-between gap-2 py-2 pr-2 [&[data-state=open]>i]:rotate-180'>
                            <div className='flex min-w-0 flex-1 items-center gap-2 truncate'>
                              {sidebarOpt[key]?.icon}
                              <span className='truncate'>{sidebarOpt[key]?.title}</span>
                            </div>
                            <ChevronDown size={16} strokeWidth={1.5} className='shrink-0 transition-transform' aria-hidden />
                          </CollapsibleTrigger>
                          <CollapsibleContent className='pl-6'>
                            {sidebarOpt[key]?.options.filter(Boolean).map((option, optIdx) => {
                              if (option.type === 'mailto') {
                                return (
                                  <a key={optIdx} href={option.href} target='_blank' className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5' style={option.style}>
                                    <div className='flex items-center gap-2.5'>
                                      {option.icon}
                                      <span>{option.title}</span>
                                    </div>
                                  </a>
                                );
                              } else if (option.type === 'accordion') {
                                return (
                                  <Collapsible key={optIdx} className='pl-2'>
                                    <CollapsibleTrigger className='flex w-full min-w-0 items-center justify-between gap-2 py-2 pr-2 [&[data-state=open]>i]:rotate-180'>
                                      <div className='flex min-w-0 flex-1 items-center gap-2 truncate'>
                                        {option?.icon}
                                        <span className='truncate'>{option?.title}</span>
                                      </div>
                                      <ChevronDown size={16} strokeWidth={1.5} className='shrink-0 transition-transform' aria-hidden />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className='pl-4'>
                                      {option?.options.map((innerOption, innerIdx) => (
                                        <Link
                                          key={innerIdx}
                                          id={innerOption.id}
                                          to='/#'
                                          className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5 [&.active]:text-sidebar-primary [&.active]:border-l-sidebar-primary [&.active]:bg-sidebar-accent'
                                          onClick={() => openModal(innerOption.to)}>
                                          <div className='flex items-center gap-2.5'>
                                            {innerOption.icon}
                                            <span>{innerOption.title}</span>
                                          </div>
                                        </Link>
                                      ))}
                                    </CollapsibleContent>
                                  </Collapsible>
                                );
                              } else if (option.type === 'video') {
                                return (
                                  <Link key={optIdx} id={option.id} onClick={() => openModal(option.link)} to='/#' className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5 [&.active]:text-sidebar-primary [&.active]:border-l-sidebar-primary [&.active]:bg-sidebar-accent'>
                                    <div className='flex items-center gap-2.5'>
                                      {option.icon}
                                      <span>{option.title}</span>
                                    </div>
                                  </Link>
                                );
                              } else {
                                return (
                                  <Link to={option.id} key={optIdx} id={option.id} className='flex items-center gap-2.5 py-2 px-6 text-[13px] font-medium text-sidebar-foreground/60 border-l-2 border-transparent transition-all duration-200 hover:text-sidebar-foreground hover:bg-white/5 [&.active]:text-sidebar-primary [&.active]:border-l-sidebar-primary [&.active]:bg-sidebar-accent'>
                                    <div className='flex items-center gap-2.5'>
                                      {option.icon}
                                      <span>{option.title}</span>
                                    </div>
                                  </Link>
                                );
                              }
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </li>
                  );
                })}
            </div>
            <div>
              {isLocal && (
                <li style={{ borderTop: '1px solid var(--sidebar-border, rgba(255,255,255,0.05))' }} className='sidebar-toggle flex flex-wrap gap-x-4 gap-y-1 px-4 py-2'>
                  <Label className='sidebar-label flex cursor-pointer items-center gap-2 text-xs'>
                    <Switch
                      className='data-[state=checked]:bg-primary data-[state=unchecked]:bg-white/30 [&>span]:bg-white'
                      checked={state.admin}
                      onCheckedChange={(checked) => {
                        setState((prev) => ({ ...prev, admin: checked }));
                        toggleAdminProfile(checked, 'cyfAdmin');
                      }}
                    />
                    Admin User
                  </Label>
                  <Label className='sidebar-label flex cursor-pointer items-center gap-2 text-xs'>
                    <Switch
                      className='data-[state=checked]:bg-primary data-[state=unchecked]:bg-white/30 [&>span]:bg-white'
                      checked={state.whiteLabelAdmin}
                      onCheckedChange={(checked) => {
                        setState((prev) => ({ ...prev, whiteLabelAdmin: checked }));
                        toggleAdminProfile(checked, 'whiteLabelAdmin');
                      }}
                    />
                    White Label Admin
                  </Label>
                </li>
              )}
              <li style={{ borderTop: '1px solid var(--sidebar-border, rgba(255,255,255,0.05))', display: showAdminPortal ? 'block' : 'none' }} className='sidebar-toggle'>
                <Label htmlFor='AdminPortal-switch' className='sidebar-label flex cursor-pointer items-center gap-2' style={{ '--sidebar-toggle-color': sidebarLinkColor }}>
                  <Switch
                    id='AdminPortal-switch'
                    className='data-[state=checked]:bg-primary data-[state=unchecked]:bg-white/30 [&>span]:bg-white'
                    checked={adminView}
                    onCheckedChange={() => setAdminView(!adminView)}
                  />
                  Admin Portal
                </Label>
              </li>
            </div>
          </ul>
        </div>
      </div>
    </aside>
  );
};

const mapStateToProps = (state) => {
  const { cyfAdmin, whiteLabelAdmin, consultantAdmin, permissions } = state.profile?.details || {};
  const canManageOrganisation = !!state?.organisation?.permissions?.organisation?.manage;
  const canAccessWfhSurveys = canManageOrganisation || !!cyfAdmin;
  const { orgMissingCompulsoryData } = state.organisation || {};
  const orgDetails = state.organisation?.details;
  const { features } = orgDetails || {};
  const canUseWfhSurvey = hasFeatureEntitlement(orgDetails, 'WFH_SURVEY');
  const hasSubscriptionActiveToUse = hasUsableSubscription(orgDetails);
  const wLSettings = state.whiteLabelAdmin;
  const furtherWL = window.location.hostname.includes('furthr');
  const showAdminPortal =
    permissions?.portal?.includes('VIEW_ADMIN') || (state.isLocalDevelopment && !!cyfAdmin);
  const isWhiteLabelApp = state.isWhiteLabelApp;
  const blockSiderBarBtn = !cyfAdmin && (!hasSubscriptionActiveToUse || orgMissingCompulsoryData);
  const kind = shellKindFromState(state);
  const orgListForHierarchy = uniqBy(
    [...(state.organisation?.list || []), ...(state.admin?.organisations || [])],
    'id',
  );
  const groupParent = isGroupParentOrg(orgDetails, orgListForHierarchy);
  const workingAsClient = isClientWorkingContext(state);
  const pointerStyle = { pointerEvents: blockSiderBarBtn ? 'none' : 'auto' };

  const myOrganisationLink = {
    type: 'link',
    id: 'myOrganisationSidebarLink',
    title: 'My organisation',
    to: '/my-organisation',
    icon: <Building2 size={16} strokeWidth={1.5} />,
  };

  const sidebarOpt = {};

  if (kind === 'consultant') {
    sidebarOpt.consultantDashboard = {
      type: 'link',
      id: 'consultantDashboardLink',
      title: 'Consultant Dashboard',
      to: '/consultant/dashboard',
      icon: <LayoutDashboard size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.consultantTasks = {
      type: 'link',
      id: 'consultantTasksLink',
      title: 'Tasks',
      to: '/consultant/tasks',
      icon: <Inbox size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.consultantImpact = {
      type: 'link',
      id: 'consultantImpactLink',
      title: 'Impact & Wins',
      to: '/consultant/impact',
      icon: <Trophy size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.clients = {
      type: 'link',
      id: 'consultantClientsLink',
      title: 'Clients',
      to: '/consultant/portfolio',
      icon: <Users size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.consultantBilling = {
      type: 'link',
      id: 'consultantBillingLink',
      title: 'Plans & billing',
      to: '/consultant/billing',
      icon: <CreditCard size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.agreements = {
      type: 'link',
      id: 'agreementsSidebarLink',
      title: 'Agreements',
      to: '/agreements',
      icon: <FileText size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.myOrganisation = myOrganisationLink;
  } else if (kind === 'whitelabel') {
    sidebarOpt.partnerClients = {
      type: 'link',
      id: 'partnerClientsLink',
      title: 'Client organisations',
      to: '/admin/organisations',
      icon: <Users size={16} strokeWidth={1.5} />,
    };
    if (orgDetails?.partnerResellerContractId) {
      sidebarOpt.partnerAccounts = {
        type: 'link',
        id: 'partnerAccountsLink',
        title: 'Partner accounts',
        to: '/partner/accounts',
        icon: <CreditCard size={16} strokeWidth={1.5} />,
      };
    }
    sidebarOpt.myOrganisation = {
      ...myOrganisationLink,
      title: 'My organisation (partner)',
    };
  } else {
    sidebarOpt.dashboard = {
      type: 'link',
      id: '',
      title: 'Dashboard',
      to: '/',
      icon: <LayoutDashboard size={16} strokeWidth={1.5} />,
      feature: 'DASHBOARD',
    };
    if (groupParent && !workingAsClient) {
      sidebarOpt.organisations = {
        type: 'link',
        id: 'orgsSidebarLink',
        title: 'Organisations',
        to: '/organisations',
        icon: <Building2 size={16} strokeWidth={1.5} />,
      };
    }
    sidebarOpt.myOrganisation = myOrganisationLink;
    sidebarOpt.sharedAsks = {
      type: 'link',
      id: 'sharedAsksSidebarLink',
      title: 'Shared asks',
      to: '/shared-asks',
      icon: <Inbox size={16} strokeWidth={1.5} />,
    };
    sidebarOpt.facilities = {
      type: 'link',
      id: 'facilitiesSidebarLink',
      title: 'Facilities',
      to: '/facilities',
      icon: <MapPin size={16} strokeWidth={1.5} />,
      style: pointerStyle,
    };
    sidebarOpt.usage = {
      type: 'link',
      id: 'usageSidebarLink',
      title: 'Usage Data',
      to: '/usage',
      icon: <BarChart3 size={16} strokeWidth={1.5} />,
      badge: state.connections?.importSummary?.needingReview || 0,
      style: pointerStyle,
    };
    if (canManageOrganisation || cyfAdmin) {
      sidebarOpt.connections = {
        type: 'link',
        id: 'connectionsSidebarLink',
        title: 'Connections',
        to: '/connections',
        icon: <Plug size={16} strokeWidth={1.5} />,
        style: pointerStyle,
      };
    }
    sidebarOpt.reports = {
      type: 'link',
      id: 'reportsSidebarLink',
      title: 'Reports',
      to: '/reports',
      icon: <FileBarChart size={16} strokeWidth={1.5} />,
      style: pointerStyle,
    };
    if (canAccessWfhSurveys && canUseWfhSurvey) {
      sidebarOpt.surveys = {
        type: 'link',
        id: 'surveysSidebarLink',
        title: 'WFH Surveys',
        to: '/surveys',
        icon: <Clipboard size={16} strokeWidth={1.5} />,
        style: pointerStyle,
      };
    }
    if (hasSubscriptionActiveToUse || cyfAdmin) {
      sidebarOpt.agreements = {
        type: 'link',
        id: 'agreementsSidebarLink',
        title: 'Agreements',
        to: '/agreements',
        icon: <FileText size={16} strokeWidth={1.5} />,
      };
    }
    const stripeCustomerIdReporting = state.payment?.stripe?.stripeCustomerId;
    if (
      orgDetails?.hasSubscriptionActive === true &&
      orgDetails?.subscriptionStatus !== 'trialing' &&
      stripeCustomerIdReporting
    ) {
      sidebarOpt.addOns = {
        type: 'link',
        id: 'addOnsSidebarLink',
        title: 'Add-Ons',
        to: '/addons',
        icon: <Package size={16} strokeWidth={1.5} />,
      };
    }
    sidebarOpt.developer = {
      type: 'link',
      id: 'developerSidebarLink',
      title: `Developer Dashboard`,
      to: '/developer',
      icon: <Terminal size={16} strokeWidth={1.5} />,
      feature: 'API_ACCESS',
    };
    sidebarOpt.targets = {
      type: 'link',
      id: 'targetsSidebarLink',
      title: 'Science-Based Targets',
      to: '/netZero',
      icon: <Target size={16} strokeWidth={1.5} />,
      feature: 'SCIENCE_BASED_TARGETS',
    };
  }

  sidebarOpt.users = {
    type: 'link',
    id: 'usersSidebarLink',
    title: 'Users',
    to: '/users',
    icon: <Users size={16} strokeWidth={1.5} />,
    style: kind ? undefined : pointerStyle,
  };
  sidebarOpt.profile = {
    type: 'link',
    id: 'profileSidebarLink',
    title: 'Your Profile',
    to: '/profile',
    icon: <CircleUser size={16} strokeWidth={1.5} />,
  };
  if (state.profile?.details?.ambassadorAdmin) {
    sidebarOpt.ambassador = {
      type: 'link',
      id: 'ambassadorSidebarLink',
      title: 'Ambassador',
      to: '/ambassador',
      icon: <HeartHandshake size={16} strokeWidth={1.5} />,
    };
  }
  const stripeCustomerId = state.payment?.stripe?.stripeCustomerId;
  // Consultants bill through Plans & billing. A saved card creates a Stripe
  // customer, which would otherwise add this direct-customer page as well.
  if (stripeCustomerId && kind !== 'consultant') {
    sidebarOpt.billing = {
      type: 'link',
      id: 'billingSidebarLink',
      title: 'Plan and Billing',
      to: '/plan-and-billing',
      icon: <CreditCard size={16} strokeWidth={1.5} />,
    };
  }

  sidebarOpt.resources = {
    type: 'accordion',
    id: 'resourcesSidebar',
    title: 'Resources and Support',
    icon: <LifeBuoy size={16} strokeWidth={1.5} />,
    options: [
      isWhiteLabelApp // White labels are shown the tutorial videos in a dropdown
        ? (() => {
            const tutorialLinks = [
              { title: 'Organisational Set Up Tutorial', to: wLSettings?.organisationSetUpTutorial },
              { title: 'Create Facilities Tutorial', to: wLSettings?.facilitiesTutorial },
              { title: 'Adding Usage Data Tutorial', to: wLSettings?.usageDataTutorial },
              { title: 'Using Import Templates Tutorial', to: wLSettings?.importTemplatesTutorial },
              { title: 'Creating Reports & Results Tutorial', to: wLSettings?.reportsTutorial },
            ].filter((link) => link.to);
            return tutorialLinks.length > 0
              ? {
                  type: 'accordion',
                  title: 'Tutorial Videos',
                  icon: <PlayCircle size={16} strokeWidth={1.5} />,
                  options: tutorialLinks.map((link) => ({
                    type: 'link',
                    ...link,
                    icon: <CirclePlay size={16} strokeWidth={1.5} />,
                  })),
                }
              : null;
          })()
        : wLSettings?.knowledgeHub
          ? {
              type: 'mailto',
              title: 'Knowledge Hub',
              href: wLSettings.knowledgeHub,
              icon: <Lightbulb size={16} strokeWidth={1.5} />,
              style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
            }
          : null,
      wLSettings?.dataHelperTool ? {
        type: 'mailto',
        title: 'Data Helper Tool',
        href: wLSettings.dataHelperTool,
        icon: <FileBarChart size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.usageDataGuide ? {
        type: 'mailto',
        title: 'Usage Data Guide',
        href: wLSettings.usageDataGuide,
        icon: <BarChart3 size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.industriesAndFacilitiesTypesGuide ? {
        type: 'mailto',
        title: 'Industries & Facility Types Guide',
        href: wLSettings.industriesAndFacilitiesTypesGuide,
        icon: <Building2 size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.activityDataInventoryFile ? {
        type: 'mailto',
        title: 'Activity Data Inventory file',
        href: wLSettings.activityDataInventoryFile,
        icon: <Clipboard size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.scienceBasedTarget ? {
        type: 'mailto',
        title: 'Science-Based Net-Zero Target Tool',
        href: wLSettings.scienceBasedTarget,
        icon: <Target size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.methodology ? {
        type: 'mailto',
        title: 'Methodology',
        href: wLSettings.methodology,
        icon: <Info size={16} strokeWidth={1.5} />,
        style: { pointerEvents: !cyfAdmin && !hasSubscriptionActiveToUse ? 'none' : 'auto', cursor: 'pointer' },
      } : null,
      wLSettings?.faq ? {
        type: 'mailto',
        title: 'FAQs',
        href: wLSettings.faq,
        icon: <CircleHelp size={16} strokeWidth={1.5} />,
      } : null,
      {
        type: 'mailto',
        title: 'Contact Support',
        href: `mailto:${wLSettings?.supportEmail}`,
        icon: <Mail size={16} strokeWidth={1.5} />,
        style: { cursor: 'pointer' },
      },
    ].filter(Boolean),
  };

  return {
    isWhiteLabelApp,
    isLocal: state.isLocalDevelopment,
    show: state.sidebar.show,
    branding: state.branding,
    hasSubscriptionActive: hasUsableSubscription(state.organisation?.details),
    cyfAdmin: cyfAdmin && 'cyfAdmin',
    whiteLabelAdmin: whiteLabelAdmin && 'whiteLabelAdmin',
    consultantAdmin: consultantAdmin && 'consultantAdmin',
    showAdminPortal,
    wLSettings,
    furtherWL,
    sidebarOpt,
    features: state.features,
    availableFeatures: features,
  };
};

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(AUTHENTICATIONACTIONS.logout()),
  toggleAdminProfile: (status, admin) => dispatch(PROFILEACTIONS.toggleAdminProfile(status, admin)),
  toggleSidebar: () => dispatch(SIDEBARACTIONS.toggleSidebar()),
  showBillingDetails: (status) => dispatch(SIDEBARACTIONS.showBillingDetails(status)),
  getImportSummary: () => dispatch(CONN_ACTIONS.getImportSummary()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
